import { tenantApi } from '@ims/remote'
import {
  BrandMark,
  Button,
  Card,
  Checkbox,
  Field,
  Input,
  PasswordInput,
  PasswordStrength,
  cx,
  useToast,
} from '@ims/ui'
import { Check, CloudOff, Gift, Receipt } from '@ims/ui/icons'
import { useEffect, useEffectEvent, useState, type FormEvent, type ReactNode } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { errorMessage } from '../hooks.ts'
import { landingResetLink } from '../lib/reset-link.ts'
import { useSession } from '../session.tsx'
import { finePointer } from '../lib/pointer.ts'
import { checkPassword, strengthOf } from '../lib/password.ts'
import {
  CODE_LENGTH,
  RESEND_WAIT,
  cleanCode,
  clock,
  codeErrorText,
  isTooSoon,
  isUnconfirmed,
} from '../lib/auth-code.ts'
import { RESET_PASSWORD_PATH } from '../session.tsx'
import { AuthPoster, type POSTER_SLIDES } from './auth-poster.tsx'
import { PRIVACY_URL, TERMS_URL } from '../lib/terms.ts'

const STEPS = ['Your account', 'Your business']

/** "1 Your account, 2 Your business" above the form while a new shop is being made. */
function StepMarker({ step }: { step: 1 | 2 }) {
  return (
    <ol className="mb-4 hidden items-center gap-2.5 lg:flex">
      {STEPS.map((label, i) => {
        const n = i + 1
        return (
          <li
            key={label}
            aria-current={n === step ? 'step' : undefined}
            className={cx(
              'flex items-center gap-1.5 text-[13px]',
              n === step ? 'font-bold text-ink' : 'font-semibold text-muted',
              i > 0 && 'before:mr-1 before:h-px before:w-7 before:bg-border',
            )}
          >
            <span
              className={cx(
                'flex size-[22px] items-center justify-center rounded-full text-xs font-bold',
                n === step
                  ? 'bg-primary text-on-fill'
                  : n < step
                    ? 'bg-success-light text-success'
                    : 'bg-soft text-muted',
              )}
            >
              {n < step ? <Check className="size-3.5" aria-label="Done" /> : n}
            </span>
            {label}
          </li>
        )
      })}
    </ol>
  )
}

/**
 * The page around a sign-in or set-up form. With `poster`, a wide screen shows the form
 * on the left and the posters on the right, and a phone shows the form alone; without
 * it, the form is a small card in the middle (reset password, invites, the picker).
 */
export function AuthLayout({
  children,
  poster,
  step,
  top,
  wide,
}: {
  children: ReactNode
  /** The poster to open on; leaving it out keeps the small centred card. */
  poster?: (typeof POSTER_SLIDES)[number]['id']
  step?: 1 | 2
  /** Beside the brand, top right (Sign out). */
  top?: ReactNode
  wide?: boolean
}) {
  if (!poster) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-background p-4">
        <div className="w-full max-w-sm">
          <div className="mb-6 flex items-center justify-center gap-2">
            <BrandMark size={40} />
            <span className="text-xl font-bold">Sano Godam</span>
          </div>
          {children}
        </div>
      </div>
    )
  }
  return (
    <div className="flex min-h-dvh bg-surface lg:p-4">
      <section className="flex min-w-0 flex-1 flex-col px-5 py-5 lg:px-6 lg:py-4">
        <div className="flex items-center gap-3">
          <BrandMark size={32} />
          <span className="text-lg font-extrabold tracking-tight">Sano Godam</span>
          <span className="ml-auto flex items-center gap-3 text-[13px] font-semibold text-muted">
            {step && <span className="lg:hidden">Step {step} of 2</span>}
            {top}
          </span>
        </div>
        <div className="flex flex-1 justify-center pt-6 lg:items-center lg:py-6">
          <div className={cx('w-full', wide ? 'max-w-[580px]' : 'max-w-[420px]')}>
            {step && <StepMarker step={step} />}
            {children}
          </div>
        </div>
      </section>
      <AuthPoster start={poster} className="hidden lg:flex" />
    </div>
  )
}

/** On a phone the posters are three words under the heading. */
function Promises() {
  const chip =
    'inline-flex h-[26px] items-center gap-1 whitespace-nowrap rounded-full bg-primary-light px-2 text-xs font-bold text-primary'
  return (
    <p className="mb-4 flex flex-wrap gap-1.5 lg:hidden">
      <span className={chip}>
        <CloudOff className="size-3.5" />
        Works offline
      </span>
      <span className={chip}>
        <Receipt className="size-3.5" />
        PAN / VAT bills
      </span>
      <span className={chip}>
        <Gift className="size-3.5" />
        90 days free
      </span>
    </p>
  )
}

/**
 * "Check your email": the 6-digit code that confirms a new account or lets a password be
 * reset. One box (it takes a pasted code and a phone's suggestion whole); the sixth digit
 * checks by itself, and another code can be asked for after a minute.
 */
function CodeForm({
  about,
  button,
  verify,
  resend,
  onOther,
}: {
  /** The sentence under the heading, with the email in it. */
  about: ReactNode
  button: string
  verify: (code: string) => Promise<void>
  resend: () => Promise<void>
  onOther: () => void
}) {
  const toast = useToast()
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // The wait is measured against the clock, not counted in ticks: a phone slows a hidden
  // tab's timers, and the person is away in their mail app for most of this minute.
  const [sentAt, setSentAt] = useState(() => Date.now())
  const [now, setNow] = useState(sentAt)
  const wait = Math.max(0, RESEND_WAIT - (now - sentAt) / 1000)
  const waiting = wait > 0

  useEffect(() => {
    if (!waiting) return
    const timer = setInterval(() => setNow(Date.now()), 500)
    return () => clearInterval(timer)
  }, [waiting])

  const check = async (value: string) => {
    if (busy) return
    if (value.length < CODE_LENGTH) {
      setError(`The code has ${CODE_LENGTH} digits.`)
      return
    }
    setBusy(true)
    setError(null)
    try {
      // A right code signs the person in, and this screen goes away.
      await verify(value)
    } catch (e) {
      setError(codeErrorText(e))
      setBusy(false)
    }
  }
  const again = async () => {
    try {
      await resend()
      setCode('')
      setError(null)
      const at = Date.now()
      setSentAt(at)
      setNow(at)
      toast.success('A new code is on its way')
    } catch (e) {
      toast.error(codeErrorText(e))
    }
  }

  return (
    <div>
      <h1 className="mb-1 text-2xl font-extrabold tracking-tight">Check your email</h1>
      <p className="mb-5 text-sm text-muted">{about}</p>
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault()
          void check(code)
        }}
      >
        <Field label={`${CODE_LENGTH}-digit code`} error={error}>
          {(id) => (
            <Input
              id={id}
              value={code}
              inputMode="numeric"
              autoComplete="one-time-code"
              autoFocus={finePointer}
              placeholder="••••••"
              className="h-14 pl-[0.55em] text-center text-[28px] font-bold tabular-nums tracking-[0.55em] placeholder:font-normal"
              onChange={(e) => {
                const next = cleanCode(e.target.value)
                setCode(next)
                setError(null)
                if (next.length === CODE_LENGTH && next !== code) void check(next)
              }}
            />
          )}
        </Field>
        <Button type="submit" className="w-full" loading={busy}>
          {busy ? 'Checking…' : button}
        </Button>
      </form>
      <p className="mt-4 flex justify-between gap-3 text-[13px] text-muted">
        {waiting ? (
          <span>
            Send a new code in <b className="tabular-nums text-ink">{clock(wait)}</b>
          </span>
        ) : (
          <button
            type="button"
            className="font-semibold text-primary hover:underline"
            onClick={() => void again()}
          >
            Send a new code
          </button>
        )}
        <button
          type="button"
          className="font-semibold text-primary hover:underline"
          onClick={onOther}
        >
          Use a different email
        </button>
      </p>
      <p className="mt-4 rounded-xl bg-soft p-3 text-xs text-muted">
        No email after a minute? Look in spam, and check the address above is right.
      </p>
    </div>
  )
}

type AuthMode = 'signIn' | 'signUp' | 'forgot' | 'code'

export function SignInScreen({ initialMode = 'signIn' }: { initialMode?: AuthMode } = {}) {
  const { signIn, signUp, sendPasswordReset, verifySignUpCode, resendSignUpCode } = useSession()
  const navigate = useNavigate()
  const toast = useToast()
  const [mode, setMode] = useState<AuthMode>(initialMode)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [fullName, setFullName] = useState('')
  const [busy, setBusy] = useState(false)

  const strength = checkPassword(password, { name: fullName, email })

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    if (mode === 'signUp' && !strength.ok) {
      toast.error(strength.reason ?? 'Choose a password')
      return
    }
    setBusy(true)
    try {
      if (mode === 'signIn') await signIn(email.trim(), password)
      else if (mode === 'forgot') {
        await sendPasswordReset(email.trim())
        // The code is typed on the reset page: a right one signs in there, and only there
        // does a signed-in person get the new-password form before the app.
        void navigate(RESET_PASSWORD_PATH, { state: { resetEmail: email.trim() } })
      } else {
        const { needsConfirmation } = await signUp(email.trim(), password, fullName.trim())
        if (needsConfirmation) setMode('code')
      }
    } catch (error) {
      // An account that never typed its code: send a fresh one and ask for it.
      if (mode === 'signIn' && isUnconfirmed(error)) {
        await resendSignUpCode(email.trim()).catch(() => {})
        setMode('code')
      } else if (mode === 'forgot' && isTooSoon(error)) {
        // Asked again within the minute: the code already sent still works.
        toast.info('We sent a code a moment ago. Use that one.')
        void navigate(RESET_PASSWORD_PATH, { state: { resetEmail: email.trim() } })
      } else toast.error(errorMessage(error))
    } finally {
      setBusy(false)
    }
  }

  if (mode === 'code') {
    return (
      <AuthLayout poster="kinds" step={1}>
        <CodeForm
          about={
            <>
              We sent a 6-digit code to <b className="text-ink">{email.trim()}</b>. It works for 10
              minutes.
            </>
          }
          button="Confirm"
          verify={(code) => verifySignUpCode(email.trim(), code)}
          resend={() => resendSignUpCode(email.trim())}
          onOther={() => setMode('signUp')}
        />
      </AuthLayout>
    )
  }

  return (
    <AuthLayout poster="kinds" step={mode === 'signUp' ? 1 : undefined}>
      <div>
        <h1 className="mb-1 text-2xl font-extrabold tracking-tight">
          {mode === 'signIn'
            ? 'Sign in'
            : mode === 'forgot'
              ? 'Reset your password'
              : 'Create your account'}
        </h1>
        <p className="mb-5 text-sm text-muted">
          {mode === 'signIn'
            ? 'Welcome back.'
            : mode === 'forgot'
              ? 'Enter your email and we’ll send you a 6-digit code.'
              : 'तपाईंको ठूलो गोदाम, डिजिटल रूपमा सानो. Billing and stock that keep working offline.'}
        </p>
        {mode === 'signUp' && <Promises />}
        <form onSubmit={submit} className="space-y-4">
          {mode === 'signUp' && (
            <Field label="Your name">
              {(id) => (
                <Input
                  id={id}
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  required
                  autoComplete="name"
                />
              )}
            </Field>
          )}
          <Field label="Email">
            {(id) => (
              <Input
                id={id}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
              />
            )}
          </Field>
          {mode !== 'forgot' && (
            <Field
              label="Password"
              labelAction={
                mode === 'signIn' && (
                  <button
                    type="button"
                    className="text-xs font-medium text-primary hover:underline"
                    onClick={() => setMode('forgot')}
                  >
                    Forgot password?
                  </button>
                )
              }
            >
              {(id) => (
                <>
                  <PasswordInput
                    id={id}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete={mode === 'signIn' ? 'current-password' : 'new-password'}
                  />
                  {mode === 'signUp' && <PasswordStrength {...strengthOf(strength)} />}
                </>
              )}
            </Field>
          )}
          <Button type="submit" className="w-full" loading={busy}>
            {mode === 'signIn' ? 'Sign in' : mode === 'forgot' ? 'Send code' : 'Create account'}
          </Button>
          {mode === 'signUp' && (
            <p className="text-center text-xs leading-5 text-muted">
              By creating an account, you agree to our{' '}
              <a
                href={TERMS_URL}
                target="_blank"
                rel="noreferrer"
                className="font-medium whitespace-nowrap text-primary underline underline-offset-2"
              >
                Terms of Service
              </a>{' '}
              and{' '}
              <a
                href={PRIVACY_URL}
                target="_blank"
                rel="noreferrer"
                className="font-medium whitespace-nowrap text-primary underline underline-offset-2"
              >
                Privacy Policy
              </a>
              .
            </p>
          )}
        </form>
      </div>
      <p className="mt-4 text-center text-sm text-muted">
        {mode === 'signIn'
          ? 'New here? '
          : mode === 'forgot'
            ? 'Remembered it? '
            : 'Already have an account? '}
        <button
          type="button"
          className="font-medium text-primary hover:underline"
          onClick={() => setMode(mode === 'signIn' ? 'signUp' : 'signIn')}
        >
          {mode === 'signIn' ? 'Create an account' : 'Sign in'}
        </button>
      </p>
    </AuthLayout>
  )
}

/** /accept-invite#token=... — the token stays in the fragment (never sent to servers). */
// One accept request per invite token: the effect can run again while the
// first request is in flight (re-renders, React dev double effects), and a
// second request would fail with "already used" after the first succeeded.
const accepting = new Map<string, Promise<{ tenant_id: string }>>()

export function AcceptInviteScreen({ onAccepted }: { onAccepted: (tenantId: string) => void }) {
  const { state, platform } = useSession()
  const navigate = useNavigate()
  const [status, setStatus] = useState<'working' | 'error'>('working')
  const [message, setMessage] = useState('')
  const token = new URLSearchParams(window.location.hash.slice(1)).get('token')
  const accepted = useEffectEvent((tenantId: string) => {
    history.replaceState(null, '', '/')
    onAccepted(tenantId)
    navigate('/', { replace: true })
  })

  useEffect(() => {
    if (state.status !== 'signedIn' || !token) return
    let active = true
    let request = accepting.get(token)
    if (!request) {
      request = tenantApi(platform.supabase).acceptInvite(token)
      accepting.set(token, request)
      // A failed request may be retried (for example after signing in as the right user).
      request.catch(() => accepting.delete(token))
    }
    request.then(
      ({ tenant_id }) => {
        if (active) accepted(tenant_id)
      },
      (error: unknown) => {
        if (!active) return
        setStatus('error')
        setMessage(errorMessage(error))
      },
    )
    return () => {
      active = false
    }
  }, [state.status, token, platform.supabase])

  if (!token) {
    return (
      <AuthLayout>
        <Card className="p-6 text-sm">This invite link is incomplete. Ask for a new invite.</Card>
      </AuthLayout>
    )
  }
  if (state.status !== 'signedIn') {
    return (
      <div>
        <div className="bg-primary-light p-3 text-center text-sm text-primary">
          Sign in (or create an account) with the email the invite was sent to, and the invite will
          be accepted.
        </div>
        <SignInScreen />
      </div>
    )
  }
  return (
    <AuthLayout>
      <Card className="p-6 text-sm">
        {status === 'working' ? 'Accepting invite…' : <p className="text-danger">{message}</p>}
      </Card>
    </AuthLayout>
  )
}

/**
 * /reset-password: the password-reset email lands here signed in (Supabase
 * reads the one-time token from the link). Expired or used links arrive
 * signed out with an error in the fragment.
 */
export function ResetPasswordScreen() {
  const { state, updatePassword, sendPasswordReset, verifyResetCode } = useSession()
  const toast = useToast()
  const navigate = useNavigate()
  // The email a code was just sent to (from the forgot form); gone after a reload.
  const sentTo = (useLocation().state as { resetEmail?: string } | null)?.resetEmail
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [signOutOthers, setSignOutOthers] = useState(true)
  const [busy, setBusy] = useState(false)
  // A used or expired link while this browser is still signed in (e.g. the
  // same link opened twice): say so, rather than quietly showing the form.
  const [overrideRefused, setOverrideRefused] = useState(false)

  if (state.status !== 'signedIn') {
    if (sentTo) {
      return (
        <AuthLayout poster="kinds">
          <CodeForm
            about={
              <>
                If an account exists for <b className="text-ink">{sentTo}</b>, we sent it a 6-digit
                code. It works for 10 minutes.
              </>
            }
            button="Continue"
            verify={(code) => verifyResetCode(sentTo, code)}
            resend={() => sendPasswordReset(sentTo)}
            onOther={() => void navigate(RESET_PASSWORD_PATH, { replace: true, state: null })}
          />
        </AuthLayout>
      )
    }
    return (
      <div>
        {landingResetLink !== 'none' && (
          <div className="bg-warning-light p-3 text-center text-sm text-warning">
            This reset link has expired or was already used. Request a new one below.
          </div>
        )}
        <SignInScreen initialMode="forgot" />
      </div>
    )
  }

  if (landingResetLink === 'refused' && !overrideRefused) {
    return (
      <AuthLayout>
        <Card className="p-6">
          <h1 className="mb-1 text-lg font-semibold">This link was already used</h1>
          <p className="mb-5 text-sm text-muted">
            Each reset link works once and for one hour. You’re still signed in as{' '}
            {state.user.email}, so you can change your password here anyway, or carry on.
          </p>
          <div className="flex flex-col gap-2">
            <Button onClick={() => setOverrideRefused(true)}>Change password anyway</Button>
            <Button variant="secondary" onClick={() => navigate('/', { replace: true })}>
              Go to the app
            </Button>
          </div>
        </Card>
      </AuthLayout>
    )
  }

  const strength = checkPassword(password, { email: state.user.email ?? '' })
  const submit = async (e: FormEvent) => {
    e.preventDefault()
    if (!strength.ok) return toast.error(strength.reason ?? 'Choose a password')
    if (password !== confirm) return toast.error('The two passwords don’t match')
    setBusy(true)
    try {
      await updatePassword(password, { signOutOthers })
      toast.success(
        signOutOthers ? 'Password updated. Other devices are signed out.' : 'Password updated',
      )
      navigate('/', { replace: true })
    } catch (error) {
      toast.error(errorMessage(error))
      setBusy(false)
    }
  }

  return (
    <AuthLayout poster="kinds">
      <div>
        <h1 className="mb-1 text-2xl font-extrabold tracking-tight">Set a new password</h1>
        <p className="mb-5 text-sm text-muted">For {state.user.email}.</p>
        <form onSubmit={submit} className="space-y-4">
          <Field label="New password">
            {(id) => (
              <>
                <PasswordInput
                  id={id}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="new-password"
                  autoFocus={finePointer}
                />
                <PasswordStrength {...strengthOf(strength)} />
              </>
            )}
          </Field>
          <Field label="Confirm new password">
            {(id) => (
              <PasswordInput
                id={id}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                autoComplete="new-password"
              />
            )}
          </Field>
          <div>
            <Checkbox
              label="Sign out on all other devices"
              checked={signOutOthers}
              onChange={(e) => setSignOutOthers(e.target.checked)}
            />
            <p className="mt-1 pl-6 text-xs text-subtle">
              Recommended if someone else might know your old password. Other phones and computers
              will need the new password; changes they haven’t synced yet are kept on them.
            </p>
          </div>
          <Button type="submit" className="w-full" loading={busy}>
            Save new password
          </Button>
        </form>
      </div>
    </AuthLayout>
  )
}
